import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../../Context/authContext';
import creditsIcon from '../../../assets/nav/credits-icon.svg';
import linkedinImg from '../../../assets/icon-linkedin-scraper.svg';
import messageImg from '../../../assets/icon-message-enrichment.svg';
import emailFinderImg from '../../../assets/icon-email-enrichment.svg';
import emailSendImg from '../../../assets/icon-email-sending.svg';
import { calculatePercentage, handleCreditsNames } from '../../../helpers/common';
import {
  StyledCreditBarRow,
  StyledCreditHolder,
  StyledCreditProgress,
  StyledCreditsBarHolder,
  StyledCreditsDropdown,
  StyledCreditsDropdownInner,
  StyledCreditsHeading,
  StyledCreditsHeadingHolder,
  StyledCreditsWrap,
} from './CreditsDropdown.styles';

const CREDIT_TYPE_ICONS = {
  linkedin_scraper: linkedinImg,
  enrichment_credits: messageImg,
  email_finder: emailFinderImg,
  email_sent: emailSendImg,
};

const getCreditProgressValue = (remaining, total) => {
  const percentage = calculatePercentage(remaining, total);
  if (percentage === '---' || Number.isNaN(Number(percentage))) {
    return '0%';
  }
  return `${percentage}%`;
};

const SidebarCredits = forwardRef(({ sideBar, navRef, onOpenChange }, ref) => {
  const [creditActive, setCreditActive] = useState(false);
  const creditRef = useRef(null);
  const creditsBtnRef = useRef(null);
  const creditsDropdownRef = useRef(null);
  const [creditsDropdownStyle, setCreditsDropdownStyle] = useState({});

  const { user } = useContextHook(AuthContext, v => ({
    user: v.user,
  }));

  const filteredUserCredits = user?.data?.userCredits?.filter(
    item => item.creditType !== 'leads_hub' && item.creditType !== 'linkedin_profile',
  );

  const setCreditOpen = useCallback(
    (isOpen) => {
      setCreditActive(isOpen);
      onOpenChange?.(isOpen);
    },
    [onOpenChange],
  );

  useImperativeHandle(ref, () => ({
    close: () => setCreditOpen(false),
  }));

  const updateCreditsDropdownPosition = useCallback(() => {
    if (!creditsBtnRef.current) {
      return;
    }

    const btnRect = creditsBtnRef.current.getBoundingClientRect();
    const dropdownHeight = creditsDropdownRef.current?.offsetHeight || 0;
    const viewportPadding = 12;
    const gap = 20;

    let top = btnRect.top + btnRect.height / 2 - dropdownHeight / 2;
    if (dropdownHeight > 0) {
      top = Math.max(
        viewportPadding,
        Math.min(top, window.innerHeight - dropdownHeight - viewportPadding),
      );
    } else {
      top = btnRect.top;
    }

    const left = btnRect.right + gap;
    const arrowTop = btnRect.top + btnRect.height / 2 - top;

    setCreditsDropdownStyle({
      '--credits-dropdown-top': `${top}px`,
      '--credits-dropdown-left': `${left}px`,
      '--credits-arrow-top': `${arrowTop}px`,
    });
  }, []);

  useLayoutEffect(() => {
    if (!creditActive) {
      setCreditsDropdownStyle({});
      return undefined;
    }

    updateCreditsDropdownPosition();
    const frameId = requestAnimationFrame(updateCreditsDropdownPosition);

    return () => cancelAnimationFrame(frameId);
  }, [creditActive, sideBar, filteredUserCredits?.length, updateCreditsDropdownPosition]);

  useEffect(() => {
    if (!creditActive) {
      return undefined;
    }

    const handleReposition = () => updateCreditsDropdownPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    const navWrapper = navRef.current?.querySelector('.nav-wrapper');
    navWrapper?.addEventListener('scroll', handleReposition);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
      navWrapper?.removeEventListener('scroll', handleReposition);
    };
  }, [creditActive, navRef, updateCreditsDropdownPosition]);

  useEffect(() => {
    const handleQuickActionsOpen = () => setCreditOpen(false);
    window.addEventListener('quick-actions-opened', handleQuickActionsOpen);
    return () => window.removeEventListener('quick-actions-opened', handleQuickActionsOpen);
  }, [setCreditOpen]);

  useEffect(() => {
    const handleClickOutsideCredit = (event) => {
      const target = event.target;
      if (
        creditRef.current
        && (!(target instanceof Node) || !creditRef.current.contains(target))
      ) {
        setCreditOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutsideCredit);
    return () => document.removeEventListener('click', handleClickOutsideCredit);
  }, [setCreditOpen]);

  const handleCreditsToggle = (e) => {
    e.stopPropagation();
    setCreditOpen(!creditActive);
  };

  return (
    <StyledCreditHolder ref={creditRef}>
      <button
        type="button"
        ref={creditsBtnRef}
        className={`btn-credits${creditActive ? ' active' : ''}`}
        onClick={handleCreditsToggle}
        id="sidebar-credits-btn"
      >
        <img src={creditsIcon} alt="credits" className="icon" />
        <span className="label">Credits</span>
        <span className="sidebar-tooltip-anchor" data-sidebar-tooltip="Credits" aria-hidden />
      </button>
      {creditActive && (
        <StyledCreditsDropdown ref={creditsDropdownRef} style={creditsDropdownStyle}>
          <StyledCreditsDropdownInner>
            {filteredUserCredits?.map((item, index) => {
              const iconSrc = CREDIT_TYPE_ICONS[item.creditType];

              return (
                <StyledCreditsBarHolder key={item.creditType || index}>
                  <StyledCreditsHeadingHolder>
                    <div className="icon-box">
                      {iconSrc ? <img src={iconSrc} width="36" alt="" /> : null}
                    </div>
                    <StyledCreditsHeading>{handleCreditsNames(item?.creditType)}</StyledCreditsHeading>
                  </StyledCreditsHeadingHolder>
                  <StyledCreditBarRow>
                    <StyledCreditProgress
                      style={{
                        '--value': getCreditProgressValue(item?.remaining, item?.total),
                      }}
                    />
                    <StyledCreditsWrap>
                      <span className="remaining">{item?.remaining}</span>/
                      <span className="total">{item?.total}</span>
                    </StyledCreditsWrap>
                  </StyledCreditBarRow>
                </StyledCreditsBarHolder>
              );
            })}
          </StyledCreditsDropdownInner>
        </StyledCreditsDropdown>
      )}
    </StyledCreditHolder>
  );
});

SidebarCredits.displayName = 'SidebarCredits';

export default SidebarCredits;
